import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, MapPin, Users, ArrowRight, Check, Archive } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArtClubPage() {
  const [classes, setClasses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, schoolsRes] = await Promise.all([
        axios.get(`${API}/classes`),
        axios.get(`${API}/schools`)
      ]);
      setClasses(classesRes.data);
      setSchools(schoolsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if a class is "available" (not full, not ended, not manually hidden)
  const isClassAvailable = (cls) => {
    // Manually hidden by admin - completely hide from public view
    if (cls.is_hidden) return { available: false, hidden: true };
    
    // Check if class is full
    const isFull = cls.is_full || (cls.enrolled_count >= cls.max_students);
    
    // Check if term has ended (if end_date is set)
    let hasEnded = false;
    if (cls.end_date) {
      try {
        const endDate = new Date(cls.end_date);
        hasEnded = endDate < new Date();
      } catch (e) {
        // Invalid date, ignore
      }
    }
    
    // Available if not full AND not ended
    return { 
      available: !isFull && !hasEnded, 
      hidden: false,
      isFull,
      hasEnded
    };
  };

  // Split classes into available and archived (full or ended)
  const availableClasses = classes.filter(cls => {
    const status = isClassAvailable(cls);
    return !status.hidden && status.available;
  });
  
  const archivedClasses = classes.filter(cls => {
    const status = isClassAvailable(cls);
    return !status.hidden && !status.available;
  });

  const starterPackItems = [
    "A3 Polyholdall (for carrying artwork)",
    "A4 Sketchbook",
    "Graphite pencil set (6 pencils)",
    "Oil pastels (set of 16)",
    "Watercolour set with brush",
    "Coloured pencils (set of 12)",
    "Acrylic paints (set of 12)",
    "Eraser & Sharpener",
    "Brush set (round and flat)"
  ];

  const handleEnrollClick = (classId) => {
    if (user) {
      navigate(`/enroll?class=${classId}`);
    } else {
      navigate('/enroll');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF6E8]">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-white border-b-2 border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              After School <span className="text-blue-600">Art Club</span>
            </h1>
            <p className="font-body text-lg text-slate-600 mb-8">
              Join our creative community! Weekly art sessions for children in Years 1-6. 
              Learn painting, drawing, and mixed media techniques in a fun, supportive environment.
            </p>
            <Link to="/enroll" className="btn-primary inline-flex items-center gap-2" data-testid="artclub-enroll">
              Enroll Your Child <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Available Classes */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-slate-900 mb-8">
            Available <span className="text-pink-500">Classes</span>
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : availableClasses.length === 0 ? (
            <div className="card-sticker text-center py-12">
              <p className="font-body text-slate-600 text-lg">No classes available at the moment. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {availableClasses.map((cls) => (
                <div key={cls.id} className="card-pricing" data-testid={`class-card-${cls.id}`}>
                  <h3 className="font-heading text-3xl font-bold text-slate-900 mb-4">{cls.name}</h3>
                  <p className="font-body text-slate-600 mb-4">{cls.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-slate-700 font-body">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span>{cls.day_of_week}s • Term Time</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700 font-body">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <span>{cls.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700 font-body">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      <span>{cls.school_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700 font-body">
                      <Users className="w-5 h-5 text-blue-600" />
                      <span>{cls.enrolled_count}/{cls.max_students} enrolled</span>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-4 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-slate-600">Term fee:</span>
                      <span className="font-heading text-3xl font-bold text-blue-600">£{cls.total_price}</span>
                    </div>
                    <p className="font-body text-sm text-slate-500 mt-1">Sibling discount available</p>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-blue-600 font-body mb-4">
                    <Check className="w-4 h-4" />
                    <span>Includes starter art pack</span>
                  </div>

                  <button 
                    onClick={() => handleEnrollClick(cls.id)}
                    className="btn-primary w-full"
                    data-testid={`enroll-${cls.id}`}
                  >
                    Enroll Now
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Past or Full Classes (Archived) */}
      {archivedClasses.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <Archive className="w-8 h-8 text-slate-400" />
              <h2 className="font-heading text-3xl font-bold text-slate-700">
                Past or Full Classes
              </h2>
            </div>
            <p className="font-body text-slate-500 mb-8">
              These classes are either full or have ended. Join the waiting list to be notified when spots open up!
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {archivedClasses.map((cls) => {
                const status = isClassAvailable(cls);
                return (
                  <div key={cls.id} className="card-pricing opacity-80" data-testid={`archived-class-${cls.id}`}>
                    <div className="absolute top-4 right-4 flex gap-2">
                      {status.isFull && (
                        <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full border-2 border-slate-900">
                          FULL
                        </span>
                      )}
                      {status.hasEnded && (
                        <span className="bg-slate-500 text-white text-xs font-bold px-3 py-1 rounded-full border-2 border-slate-900">
                          ENDED
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-slate-700 mb-4">{cls.name}</h3>
                    <p className="font-body text-slate-500 mb-4">{cls.description}</p>
                    
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center gap-2 text-slate-600 font-body">
                        <Calendar className="w-5 h-5 text-slate-400" />
                        <span>{cls.day_of_week}s • Term Time</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600 font-body">
                        <Clock className="w-5 h-5 text-slate-400" />
                        <span>{cls.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600 font-body">
                        <MapPin className="w-5 h-5 text-slate-400" />
                        <span>{cls.school_name}</span>
                      </div>
                    </div>

                    {status.isFull && (
                      <Link 
                        to={`/waiting-list?class=${cls.id}`}
                        className="btn-accent w-full text-center"
                        data-testid={`join-waitlist-${cls.id}`}
                      >
                        Join Waiting List
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Starter Pack */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl font-bold text-slate-900 mb-6">
                Starter Art Pack <span className="text-yellow-500">Included!</span>
              </h2>
              <p className="font-body text-lg text-slate-600 mb-6">
                Every child receives a quality starter art pack, included in the term fee. 
                Everything they need to start their creative journey!
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {starterPackItems.map((item) => (
                  <li key={item} className="flex items-center gap-2 font-body text-slate-700">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-blue-600" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="font-body text-sm text-slate-500 mt-4">
                Pencil cases available as an add-on in our shop.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c0c1ef95-0a19-424c-a3a9-3f19c962704a/artifacts/1jjknyvo_Sketchbook.jpg.webp"
                alt="Sketchbook"
                className="rounded-2xl border-2 border-slate-900 shadow-sticker h-40 w-full object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_c0c1ef95-0a19-424c-a3a9-3f19c962704a/artifacts/ftx5kvni_graphite%20pencils.jpg.webp"
                alt="Pencils"
                className="rounded-2xl border-2 border-slate-900 shadow-sticker h-40 w-full object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=300&h=200&fit=crop"
                alt="Art Supplies"
                className="rounded-2xl border-2 border-slate-900 shadow-sticker h-40 w-full object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1560421683-6856ea585c78?w=300&h=200&fit=crop"
                alt="Watercolours"
                className="rounded-2xl border-2 border-slate-900 shadow-sticker h-40 w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-slate-900 mb-4">
            Ready to join?
          </h2>
          <p className="font-body text-lg text-slate-600 mb-8">
            Enroll your child in our after-school art club and watch their creativity flourish.
          </p>
          <Link to="/enroll" className="btn-primary inline-flex items-center gap-2" data-testid="cta-enroll">
            Start Enrollment <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
